import { isEqual, omit, pick } from "lodash";
import { devtools, persist, PersistStorage, StorageValue } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createWithEqualityFn } from "zustand/traditional";
import { AppointmentState, createAppointmentSlice } from "./appointmentSlice";
import { AppState, createAppSlice } from "./appSlice";
import { ChainState, createChainSlice } from "./chainSlice";
import { createCurrentFormSlice, FormState, sessionStateKeys } from "./currentFormSlice";

export type State = AppState & FormState & AppointmentState & ChainState;

const readStored = (storage: () => Storage, name: string): StorageValue<Partial<State>> | null => {
  try {
    const raw = storage().getItem(name);
    return raw ? (JSON.parse(raw) as StorageValue<Partial<State>>) : null;
  } catch {
    return null;
  }
};

const writeStored = (storage: () => Storage, name: string, value: StorageValue<Partial<State>>) => {
  try {
    storage().setItem(name, JSON.stringify(value));
  } catch {
    // Blocked storage: the state lives in memory until the tab closes.
  }
};

/**
 * The session lives in one tab, so does its state (the chosen record, the form in progress, the scope): two
 * tabs on a shared phone must never continue on each other's record. Link, language and booking state stay
 * on the device, and so does which forms each record submitted today.
 */
const tabScopedStorage: PersistStorage<Partial<State>> = {
  getItem: (name) => {
    const device = readStored(() => localStorage, name);
    const session = readStored(() => sessionStorage, name);
    if (!device && !session) return null;
    return {
      state: { ...omit(device?.state, sessionStateKeys), ...pick(session?.state, sessionStateKeys) },
      version: device?.version ?? session?.version,
    };
  },
  setItem: (name, { state, version }) => {
    writeStored(() => localStorage, name, { state: omit(state, sessionStateKeys), version });
    writeStored(() => sessionStorage, name, { state: pick(state, sessionStateKeys), version });
  },
  removeItem: (name) => {
    for (const storage of [() => localStorage, () => sessionStorage]) {
      try {
        storage().removeItem(name);
      } catch {
        // nothing to remove
      }
    }
  },
};

export const useMainStore = createWithEqualityFn<State>()(
  immer(
    devtools(
      persist(
        (...a) => ({
          ...createAppSlice(...a),
          ...createCurrentFormSlice(...a),
          ...createAppointmentSlice(...a),
          ...createChainSlice(...a),
        }),
        {
          name: "store",
          storage: tabScopedStorage,
          // The auth mirror belongs to the tab's Firebase session (a persisted loggingOut would keep
          // MainLayout on its spinner), and a dialog cannot outlive its resolve function: restored on the
          // next start, its buttons would do nothing.
          partialize: (state) =>
            omit(state, ["loggedIn", "userId", "loggingOut", "authReady", "confirmationModalData"]),
        },
      ),
      {
        name: "PhysioFit Store",
        enabled: true,
      },
    ),
  ),
  isEqual,
);

export const resetStore = (state: State) => {
  state.resetAppState();
  state.resetCurrentFormState();
  state.resetAppointmentState();
  state.resetChainState();
};

export const deleteCurrentUserData = (state: State) => {
  state.resetCurrentFormStateWithoutUser();
  state.resetAppointmentState();
  // An open dialog can show the signed-out person's data, and its caller waits for an answer.
  state.dismissConfirmationModal();
};
